import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import appearanceService from '../services/appearanceService';

const AppearanceContext = createContext(null);

// Maps settings -> CSS custom properties on :root (applied at runtime, no rebuild).
const COLOR_VARS = {
  color_primary: '--color-primary',
  color_secondary: '--color-secondary',
  color_accent: '--color-accent',
  color_background: '--color-background',
  color_surface: '--color-surface',
  color_text: '--color-text',
  color_text_muted: '--color-text-muted',
  color_success: '--color-success',
  color_danger: '--color-danger',
};

export function applyAppearance(settings) {
  if (!settings) return;
  const root = document.documentElement;
  Object.entries(COLOR_VARS).forEach(([field, cssVar]) => {
    if (settings[field]) root.style.setProperty(cssVar, settings[field]);
  });
  if (settings.radius != null) root.style.setProperty('--app-radius', settings.radius);
  if (settings.font_family) {
    root.style.setProperty('--app-font', settings.font_family);
    document.body.style.fontFamily = settings.font_family;
  }
  // Global text size: scale the root font-size; Tailwind's rem-based sizes
  // (text-sm, p-4, etc.) all scale proportionally across the whole app.
  const scale = parseFloat(settings.font_scale || '1') || 1;
  root.style.fontSize = `${16 * scale}px`;
  if (settings.app_name) document.title = settings.app_name;
}

export function AppearanceProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Preview mode: when the control panel embeds this app with ?preview=draft,
  // load the DRAFT settings instead of the published ones.
  const isPreviewDraft = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('preview') === 'draft';

  const refresh = useCallback(async () => {
    try {
      const data = await appearanceService.getSettings(isPreviewDraft ? 'draft' : 'published');
      setSettings(data);
      applyAppearance(data);
      return data;
    } catch (err) {
      console.error('Failed to load appearance settings:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // In preview mode, re-fetch the draft when the panel reloads the frame.
  useEffect(() => {
    if (!isPreviewDraft) return undefined;
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isPreviewDraft, refresh]);

  // Live preview: apply a working copy to the DOM without persisting.
  const previewAppearance = useCallback((working) => {
    applyAppearance(working);
  }, []);

  const value = {
    settings,
    loading,
    refresh,
    setSettings,
    previewAppearance,
    // Convenience accessors for content/layout overrides.
    text: (key, fallback = '') =>
      (settings && settings.content && settings.content[key] != null
        ? settings.content[key]
        : fallback),
    flag: (key, fallback = false) =>
      (settings && settings.layout && settings.layout[key] != null
        ? settings.layout[key]
        : fallback),
  };

  return (
    <AppearanceContext.Provider value={value}>
      {children}
    </AppearanceContext.Provider>
  );
}

export function useAppearance() {
  const ctx = useContext(AppearanceContext);
  if (!ctx) {
    // Safe fallback if used outside the provider.
    return {
      settings: null,
      loading: false,
      refresh: () => {},
      setSettings: () => {},
      previewAppearance: () => {},
      text: (_k, fb = '') => fb,
      flag: (_k, fb = false) => fb,
    };
  }
  return ctx;
}

export default AppearanceContext;
